/**
 * His vendor-per-material list (XC-INV2PO DB6 note §3): `GET /api/v1/business-profile/ordering/vendors`,
 * read by the Business Profile's "How you order" card and the Inbox's "who supplies it?" cards.
 *
 * A row is one material of his count: its vendor (settled) or, while it is open, the vendors it may
 * be (`choices`, his words). `reason` is why Wingo proposed it so; `group` and `held` are sent back
 * unchanged with a save (the server checks a save carries exactly its list's materials, words,
 * groups and choices) and are never printed. A payload that fails the contract paints nothing.
 */

import { z } from 'zod';

export const VendorRowSchema = z.object({
  key: z.string().min(1),
  material: z.string().min(1),
  vendor: z.string().nullable(),
  choices: z.array(z.string()),
  group: z.string().nullable(),
  held: z.string().nullable(),
  open: z.boolean(),
  reason: z.string().nullable(),
  changed: z.boolean(),
});

export const VendorListSchema = z.object({
  state: z.enum(['none', 'proposed', 'confirmed', 'withdrawn']),
  ruling_ref: z.string().min(1),
  kind: z.literal('business_policy'),
  subtype: z.literal('vendor_per_material'),
  based_on: z.string().nullable(),
  proposed: z
    .object({ at: z.string(), count_date: z.string().nullable(), materials: z.number().int() })
    .nullable(),
  confirmed: z.object({ at: z.string() }).nullable(),
  rows: z.array(VendorRowSchema),
  vendors: z.array(z.string()),
  counts: z.object({
    materials: z.number().int(),
    settled: z.number().int(),
    open: z.number().int(),
  }),
  undo: z.object({ ruling_ref: z.string(), undoes: z.string(), at: z.string() }).nullable(),
  recent: z.array(
    z.object({ at: z.string(), material: z.string(), vendor: z.string().nullable() }),
  ),
  /** Withheld whole for a role whose view hides vendor records: no row or vendor name, the counts kept. */
  hidden_for_role: z.boolean().optional().default(false),
  can_edit: z.boolean(),
  edit_reason: z.string().nullable(),
});

export type VendorRow = z.infer<typeof VendorRowSchema>;
export type VendorList = z.infer<typeof VendorListSchema>;

/** One row of his list as a save sends it: the read row's own fields, his vendor or still open. */
export interface VendorEntry {
  key: string;
  material: string;
  vendor?: string;
  group?: string;
  choices?: string[];
  held?: string;
}

/** The value a save files: every row of the list, `changes` (key -> vendor) applied. */
export function listValue(list: VendorList, changes: Record<string, string>): { entries: VendorEntry[] } {
  return {
    entries: list.rows.map((row) => {
      const entry: VendorEntry = { key: row.key, material: row.material };
      if (row.group) entry.group = row.group;
      if (row.choices.length > 0) entry.choices = [...row.choices];
      const chosen = changes[row.key]?.trim();
      if (chosen) entry.vendor = chosen;
      else if (row.vendor) entry.vendor = row.vendor;
      else if (row.held) entry.held = row.held;
      return entry;
    }),
  };
}

/** How many questions his Inbox owes on the list: one "review it" before he confirms, then one per
 * open row (the sidebar badge and the Inbox header read the same number). */
export function vendorQuestionCount(list: VendorList | undefined): number {
  if (!list || list.rows.length === 0) return 0;
  return list.state === 'confirmed' ? list.counts.open : 1;
}
